import { useCallback, useMemo } from "react";
import { VariantGroup } from "@danishagro/shared/src/interfaces/productv2.interface";
import { DA_ProductPrice } from "@danishagro/shared/src/interfaces/price.interface";

/**
 * Shared hook for determining whether a variant option leads to a discounted variant.
 * Used in ProductDetails and ProductDetailsSiloAndHouse.
 */
export const useOptionHasDiscount = (
    price: DA_ProductPrice | undefined,
    optionGroups: VariantGroup[],
    selectedVariants: Record<string, string>
) => {
    const discountVariantIds = useMemo(
        () => new Set<string>(price?.variantDiscounts ?? []),
        [price?.variantDiscounts]
    );

    /**
     * Returns true if this option leads to a discounted variant.
     * Filters variant IDs by previous group selections so a discount
     * tag only appears when it's valid for the already-chosen combination.
     */
    const optionHasDiscount = useCallback(
        (groupIndex: number, entityIds: string[]): boolean => {
            if (discountVariantIds.size === 0) return false;

            let eligible = entityIds;
            for (let i = 0; i < groupIndex; i++) {
                const ancestor = optionGroups[i];
                const selectedValue = selectedVariants[ancestor.name];
                if (!selectedValue) return false;

                const ancestorOption = Object.values(ancestor.options).find(
                    (opt) => opt.value === selectedValue
                );
                if (!ancestorOption) return false;

                eligible = eligible.filter((id) => ancestorOption.entityIds.includes(id));
            }

            return eligible.some((id) => discountVariantIds.has(id));
        },
        [discountVariantIds, optionGroups, selectedVariants]
    );

    return { discountVariantIds, optionHasDiscount };
};
